import { InfoOutlined } from "@mui/icons-material";
import { ClickAwayListener, Button, Tooltip, Dialog } from "@mui/material";
import cx from "classnames";
import { useFormik, Field, FormikProvider } from "formik";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import * as Yup from "yup";

import { ReactComponent as ArrowUp } from "assets/arrow-up.svg";
import { ReactComponent as ArrowDown } from "assets/dropdown-icon.svg";
import { ReactComponent as XMark } from "assets/x-mark.svg";
import ShotContext from "components/Shot/ShotContext";
import { getCutouts } from "services/CutoutService";
import { getLayers } from "services/LayerService";
import { getShotDetail, runTrackingInference } from "services/ShotService";
import { formatDate } from "utils/helper";
import { useCountdown } from "utils/hooks";

import styles from "./TrackingSidepanel.module.scss";
import { DropdownField } from "../DropdownField";
import { InputField } from "../InputField";

export const AiIcon = () => {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="aiIconGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{ stopColor: "#6565ec" }} />
          <stop offset="18.5%" style={{ stopColor: "#009efa" }} />
          <stop offset="39%" style={{ stopColor: "#00c9a7" }} />
          <stop offset="59%" style={{ stopColor: "#ffc75f" }} />
          <stop offset="79.5%" style={{ stopColor: "#ff8066" }} />
          <stop offset="100%" style={{ stopColor: "#d65db1" }} />
        </linearGradient>
      </defs>
      <g clipPath="url(#clip0_1692_12226)">
        <path
          d="M12.6667 5.99959L13.5 4.16626L15.3333 3.33293L13.5 2.49959L12.6667 0.66626L11.8333 2.49959L10 3.33293L11.8333 4.16626L12.6667 5.99959Z"
          className={styles.aiIcon}
        />
        <path
          d="M12.6667 9.99927L11.8333 11.8326L10 12.6659L11.8333 13.4993L12.6667 15.3326L13.5 13.4993L15.3333 12.6659L13.5 11.8326L12.6667 9.99927Z"
          className={styles.aiIcon}
        />
        <path
          d="M7.66602 6.33268L5.99935 2.66602L4.33268 6.33268L0.666016 7.99935L4.33268 9.66602L5.99935 13.3327L7.66602 9.66602L11.3327 7.99935L7.66602 6.33268ZM6.65935 8.65935L5.99935 10.1127L5.33935 8.65935L3.88602 7.99935L5.33935 7.33935L5.99935 5.88602L6.65935 7.33935L8.11268 7.99935L6.65935 8.65935Z"
          className={styles.aiIcon}
        />
      </g>
      <defs>
        <clipPath id="clip0_1692_12226">
          <rect width="16" height="16" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

interface TrackingSidePanelProps {
  handleClose: () => void;
  setIsTracking: React.Dispatch<React.SetStateAction<boolean>>;
  disabled?: boolean;
}

const TrackingSidePanel: React.FC<TrackingSidePanelProps> = ({
  handleClose,
  setIsTracking,
  disabled,
}) => {
  const {
    selectedShot,
    service,
    setShots,
    setLayers,
    setShotCutouts,
    throttleUsage,
    getThrottleUsage,
  } = useContext(ShotContext);
  const [exiting, setExiting] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  const throttledTill = useMemo(() => {
    if (
      throttleUsage?.is_inference_throttled &&
      (throttleUsage.allowed_inferences || 0) < 1 &&
      throttleUsage.next_inference_provision_at
    ) {
      return new Date(throttleUsage.next_inference_provision_at);
    }
    return null;
  }, [throttleUsage]);
  const throttleCountdown = useCountdown(throttledTill, getThrottleUsage);

  const closeSidepanel = () => {
    setExiting(true);
    setTimeout(() => {
      handleClose();
      setExiting(false);
    }, 300);
  };

  const formik = useFormik({
    initialValues: {
      workingFps: selectedShot?.fps || "",
      lens: "",
      sensorWidth: "",
      sensorHeight: "",
      estimatedClosestPoint: "",
      estimatedFarthestPoint: "",
      userPixelAspectRatio: "",
      calculateDistortion: "False",
      fixFocalLength: "False",
      fixSensorSize: "True",
    },
    validationSchema: Yup.object({
      sensorWidth: Yup.string().test(
        "width-required",
        "Sensor width required",
        (value, context) => {
          const { sensorHeight } = context.parent;
          const hasHeight = sensorHeight && sensorHeight.trim().length > 0;
          const hasWidth = value && value.trim().length > 0;

          return !(hasHeight && !hasWidth);
        },
      ),
      sensorHeight: Yup.string().test(
        "height-required",
        "Sensor height required",
        (value, context) => {
          const { sensorWidth } = context.parent;
          const hasWidth = sensorWidth && sensorWidth.trim().length > 0;
          const hasHeight = value && value.trim().length > 0;

          return !(hasWidth && !hasHeight);
        },
      ),
    }),
    onSubmit: async (values) => {
      if (!selectedShot) return;
      setIsTracking(true);
      runTrackingInference(selectedShot.id, values)
        .then((res) => {
          getLayers(selectedShot.id, service).then((result) => {
            setLayers(result);
          });
          getCutouts(undefined, selectedShot.id, service).then((result) => {
            setShotCutouts(result);
          });

          getShotDetail(selectedShot.id).then((shot) => {
            setShots((oldShots) => {
              const newShots = { ...oldShots };
              const key = formatDate(shot.created);
              const shotIndex = newShots[key].findIndex(
                (s) => s.id === shot.id,
              );
              if (shotIndex !== -1) {
                newShots[key][shotIndex] = shot;
                return newShots;
              }
              return oldShots;
            });
          });
          closeSidepanel();
        })
        .catch((error) => {
          setIsTracking(false);
          if (error.message?.startsWith("There doesn't")) {
            setErrorMsg(error.message);
          } else {
            toast.error(error.message);
            closeSidepanel();
          }
        })
        .finally(() => {
          setTimeout(() => {
            setIsTracking(false);
          }, 6000);
        });
    },
  });

  return (
    <ClickAwayListener
      onClickAway={(e) => {
        closeSidepanel();
      }}
    >
      <div
        className={cx(
          styles.trackingSidepanel,
          exiting ? styles.panelExiting : styles.panelVisible,
        )}
      >
        <div className={styles.panelHeader}>
          <h3>Camera Info</h3>
          <button
            type="button"
            className={styles.closeButton}
            onClick={() => {
              closeSidepanel();
            }}
          >
            <XMark />
          </button>
        </div>
        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit}>
            <div className={styles.innerContainer}>
              <div className={styles.fields}>
                <div>
                  <button
                    type="button"
                    onClick={() => {
                      setIsExpanded(!isExpanded);
                    }}
                    className={styles.advancedSettingsButton}
                  >
                    Camera Details <span>(Optional)</span>
                    {isExpanded ? (
                      <ArrowUp className={styles.arrowIcon} />
                    ) : (
                      <ArrowDown className={styles.arrowIcon} />
                    )}
                  </button>
                  <div
                    className={styles.expandable}
                    style={{
                      maxHeight: isExpanded ? `435px` : "0px",
                      marginTop: isExpanded ? "16px" : "0px",
                    }}
                  >
                    <div className={styles.settings}>
                      <div className={styles.row}>
                        <Field
                          name="workingFps"
                          component={InputField}
                          type="number"
                          label="Working FPS"
                          className={styles.panelInput}
                          inputClassName={styles.input}
                        />
                        <div className={styles.inputAndDropdownFieldContainer}>
                          <Field
                            name="lens"
                            component={InputField}
                            type="number"
                            label="Lens"
                            className={styles.inputAndDropdownFieldPanelInput}
                            suffix="mm"
                            suffixClassName={styles.inputAndDropdownFieldSuffix}
                            inputClassName={styles.inputAndDropdownFieldInput}
                          />
                          <Field
                            name="fixFocalLength"
                            component={DropdownField}
                            label="DD"
                            labelClassName={styles.inputAndDropdownFieldLabel}
                            className={
                              styles.inputAndDropdownFieldPanelDropdown
                            }
                            selectClassName={
                              styles.inputAndDropdownFieldDropdown
                            }
                          >
                            <option value="False">Floating</option>
                            <option value="True">Fixed</option>
                          </Field>
                        </div>
                      </div>
                      <div className={styles.row}>
                        <div className={styles.inputAndDropdownFieldContainer}>
                          <Field
                            name="sensorWidth"
                            component={InputField}
                            label="Sensor Width"
                            className={styles.inputAndDropdownFieldPanelInput}
                            type="number"
                            suffix="mm"
                            suffixClassName={styles.inputAndDropdownFieldSuffix}
                            inputClassName={styles.inputAndDropdownFieldInput}
                            errorClassName={styles.inputAndDropdownFieldError}
                          />
                          <Field
                            name="fixSensorSize"
                            component={DropdownField}
                            label="DD"
                            labelClassName={styles.inputAndDropdownFieldLabel}
                            className={
                              styles.inputAndDropdownFieldPanelDropdown
                            }
                            selectClassName={
                              styles.inputAndDropdownFieldDropdown
                            }
                          >
                            <option value="True">Fixed</option>
                            <option value="False">Floating</option>
                          </Field>
                        </div>
                        <div className={styles.inputAndDropdownFieldContainer}>
                          <Field
                            name="sensorHeight"
                            component={InputField}
                            label="Sensor Height"
                            className={styles.inputAndDropdownFieldPanelInput}
                            type="number"
                            suffix="mm"
                            suffixClassName={styles.inputAndDropdownFieldSuffix}
                            inputClassName={styles.inputAndDropdownFieldInput}
                            errorClassName={styles.inputAndDropdownFieldError}
                          />
                          <Field
                            name="fixSensorSize"
                            component={DropdownField}
                            label="DD"
                            labelClassName={styles.inputAndDropdownFieldLabel}
                            className={
                              styles.inputAndDropdownFieldPanelDropdown
                            }
                            selectClassName={
                              styles.inputAndDropdownFieldDropdown
                            }
                          >
                            <option value="True">Fixed</option>
                            <option value="False">Floating</option>
                          </Field>
                        </div>
                      </div>
                    </div>
                    <div className={styles.cameraDetails}>
                      <h3 className={styles.settingsTitle}>Scene Scale</h3>
                      <div className={styles.settings}>
                        <div className={styles.row}>
                          <Field
                            name="estimatedClosestPoint"
                            component={InputField}
                            label={
                              <div className={styles.labelContainer}>
                                <div>Estimated closest point</div>
                                <Tooltip
                                  arrow
                                  slotProps={{
                                    tooltip: {
                                      sx: {
                                        padding: "0px",
                                        backgroundColor: "unset",
                                        fontFamily: "Inter",
                                      },
                                    },
                                    arrow: {
                                      sx: {
                                        color: "#22242A",
                                      },
                                    },
                                  }}
                                  placement="top"
                                  title={
                                    <div className={styles.tooltip}>
                                      Estimated distance from the camera to the
                                      closest point you are tracking. Rough
                                      estimate will do.
                                    </div>
                                  }
                                >
                                  <InfoOutlined className={styles.infoIcon} />
                                </Tooltip>
                              </div>
                            }
                            className={styles.panelInput}
                            type="number"
                            suffix="m"
                            suffixClassName={styles.suffix}
                            inputClassName={styles.input}
                          />
                          <Field
                            name="estimatedFarthestPoint"
                            component={InputField}
                            label={
                              <div className={styles.labelContainer}>
                                <div>Estimated farthest point</div>
                                <Tooltip
                                  arrow
                                  slotProps={{
                                    tooltip: {
                                      sx: {
                                        padding: "0px",
                                        backgroundColor: "unset",
                                        fontFamily: "Inter",
                                      },
                                    },
                                    arrow: {
                                      sx: {
                                        color: "#22242A",
                                      },
                                    },
                                  }}
                                  placement="top"
                                  title={
                                    <div className={styles.tooltip}>
                                      Estimated distance from the camera to the
                                      farthest point you are tracking. Rough
                                      estimate will do.
                                    </div>
                                  }
                                >
                                  <InfoOutlined className={styles.infoIcon} />
                                </Tooltip>
                              </div>
                            }
                            className={styles.panelInput}
                            type="number"
                            suffix="m"
                            suffixClassName={styles.suffix}
                            inputClassName={styles.input}
                          />
                        </div>
                      </div>
                    </div>
                    <div className={styles.cameraDetails}>
                      <h3 className={styles.settingsTitle}>Lens Distortion</h3>
                      <div className={styles.distortionRow}>
                        <Field
                          name="calculateDistortion"
                          component={DropdownField}
                          label={
                            <div className={styles.labelContainer}>
                              <div>Calculate Distortion</div>
                              <Tooltip
                                arrow
                                slotProps={{
                                  tooltip: {
                                    sx: {
                                      padding: "0px",
                                      backgroundColor: "unset",
                                      fontFamily: "Inter",
                                    },
                                  },
                                  arrow: {
                                    sx: {
                                      color: "#22242A",
                                    },
                                  },
                                }}
                                placement="top"
                                title={
                                  <div className={styles.tooltip}>
                                    Turn this on to calculate lens distortion in
                                    your camera solve. We will provide ST maps
                                    with your export for the distortion used.
                                  </div>
                                }
                              >
                                <InfoOutlined className={styles.infoIcon} />
                              </Tooltip>
                            </div>
                          }
                          className={styles.panelInput}
                          selectClassName={styles.dropdown}
                        >
                          <option value="True">True</option>
                          <option value="False">False</option>
                        </Field>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles.actionContainer}>
                <Button
                  className={styles.inferenceBtn}
                  disabled={disabled}
                  type="submit"
                >
                  <AiIcon />
                  <span>
                    {throttleCountdown
                      ? `Wait for ${throttleCountdown}`
                      : "Track Camera"}
                  </span>
                </Button>
              </div>
            </div>
          </form>
        </FormikProvider>

        <Dialog
          open={!!errorMsg}
          slotProps={{
            paper: {
              className: "dialog",
            },
          }}
        >
          <div className="modalContainer">
            <div className="modalHeader">
              <h3 className="modalTitle">Selection missing on Keyframe</h3>
              <button
                onClick={() => {
                  setErrorMsg("");
                  closeSidepanel();
                }}
                type="button"
                className="closeButton"
              >
                <XMark />
              </button>
            </div>
            <div className={styles.confirmationText}>
              <p>{errorMsg}</p>
            </div>
            <div className="modalActions">
              <Button
                type="button"
                className={styles.noBackgroundButton}
                onClick={() => {
                  setErrorMsg("");
                  closeSidepanel();
                }}
              >
                Close
              </Button>
            </div>
          </div>
        </Dialog>
      </div>
    </ClickAwayListener>
  );
};

export default TrackingSidePanel;
